import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { MentionSearchModel } from '../../models/mention-search.model';
import { SaveTopicDialogLocalizationsModel } from '../../models/save-topic-dialog-localizations.model';
import { SaveTopicModeEnum } from '../../enums/save-topic-mode.enum';
import { TopicsService } from '../../../Core/services/topics.service';
import { Observable, Subscription } from 'rxjs';
import { MentionSearchListItemModel } from '../../models/mention-search-list-item.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { FormControl, FormGroup } from '@angular/forms';
import { DisplayGroupModel } from '../../../../Common/Layout/models/display-group.model';
import { AppearCondition, AutocompleteCustomOption } from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { DisplayGroupsService } from '../../../../Common/Layout/services/display-groups.service';
import { AutoSavingConfigModel } from '@Monitor/Shared/models/auto-saving-config.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { Moment } from 'moment';

@Component({
    selector: 'agpr-save-topic-dialog',
    templateUrl: './save-topic-dialog.component.html',
    styleUrls: ['./save-topic-dialog.component.scss'],
    standalone: false
})
export class SaveTopicDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    @Input() set search(search: MentionSearchModel) {
        this.searchModelChanged(search);
    }
    @Input() set saveAsNew(saveAsNew: boolean) {
        this._saveAsNew = saveAsNew;
        this.cloneSearch();
        this.setLocalizations();
    }
    @Input() id: string = 'saveTopicDialog';
    @Input() currentGroup: number;

    readonly buttonTypes = ButtonTypesEnum;

    _saveAsNew: boolean = false;
    _search: MentionSearchModel = new MentionSearchModel();

    topics: MentionSearchListItemModel[];

    localizations: SaveTopicDialogLocalizationsModel;
    accountAutoTaggingMaxReached: boolean;
    autoTagTopic: boolean = false;
    maxNumberOfTags: number;

    savingTagOptions: SaveTopicModeEnum[] = [];

    showLoading: boolean;
    tagIdsToAutoTag: number[] = [];
    topicName: string = '';
    searchIntent: string = '';
    hasAgent: boolean = false;
    nameIsValid: boolean = true;
    tagIsValid: boolean = true;
    submitted: boolean = false;
    workflowSettingsForm: FormGroup = new FormGroup({});
    private subscriptions: Subscription[] = [];

    addToGroup: boolean = false;
    groupOptions: Observable<DisplayGroupModel[]>;
    selectedGroup: DisplayGroupModel;
    public autocompleteCustomOptions: AutocompleteCustomOption[] = [
        {
            label: 'Common.lhp.groupSelectionDialog.autocomplete.createGroup',
            clearOnSelect: false,
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            action: (name: string) => this.createNewGroup(name)
        }
    ];

    constructor(
        coreDialogService: CoreDialogService,
        private topicsService: TopicsService,
        private displayGroupsService: DisplayGroupsService,
        private currentUserStateService: CurrentUserStateService
    ) {
        super(coreDialogService, 'SaveTopic');
        this.maxNumberOfTags = this.topicsService.numberOfTagsPerTopic;
        this.workflowSettingsForm = new FormGroup({
            topicWorkFlowSettings: new FormGroup({
                autoTag: new FormGroup({
                    isAutoTaggingSearch: new FormControl<boolean>(null),
                    tagIds: new FormControl<number[]>([])}
                ),
                autoSave: new FormGroup({
                    saveEnabled: new FormControl<boolean>(false),
                    startDate: new FormControl<Moment> (null),
                    endDate: new FormControl<Moment> (null),
                    tagIds: new FormControl<number[]> ([]),
                    folderIds: new FormControl<number[]> ([]),
                    timezone: new FormControl<string> (null),
                })
            }),
        });
        this.setLocalizations();

        this.groupOptions = this.displayGroupsService.getGroups();

        this.subscriptions.push(
            this.currentUserStateService.getPreferences().subscribe(() => {
                this.hasAgent = this.currentUserStateService.agenticFeatureEnabled();
            }),
            this.topicsService.getTopics().subscribe(topics => this.topics = topics),
            this.topicWorkFlowSettings.valueChanges.subscribe(formValues => {
                this._search.autoTaggingSearch = formValues.autoTag.isAutoTaggingSearch;
                this._search.applyTagIds = formValues.autoTag.tagIds;

                this._search.autoSavingConfig = new AutoSavingConfigModel(this._search.toApiModel());
                this._search.autoSavingConfig.enabled = formValues.autoSave.saveEnabled;
                this._search.autoSavingConfig.folderIds = formValues.autoSave.folderIds;
                this._search.autoSavingConfig.tagIds = formValues.autoSave.tagIds;
                this._search.autoSavingConfig.startDate = formValues.autoSave.startDate;
                this._search.autoSavingConfig.endDate = formValues.autoSave.endDate;
                this._search.autoSavingConfig.timezone = formValues.autoSave.timezone;

            }),
            this.topicsService.getTaggingTopicsLimitReached().subscribe(
                (autoTaggingLimitReached) => {
                    this.accountAutoTaggingMaxReached = autoTaggingLimitReached;
                }
            )
        );
    }

    ngOnInit(): void {
        if (!!this.currentGroup) {
            this.subscriptions.push(
                this.displayGroupsService.getGroups().subscribe(groups => {
                    this.selectedGroup = groups.find(group => group.id === this.currentGroup);
                })
            );
        }
    }

    get autoTags(): FormGroup {
        return this.workflowSettingsForm.get('topicWorkFlowSettings.autoTag') as FormGroup;
    }

    get autoSave(): FormGroup {
        return this.workflowSettingsForm.get('topicWorkFlowSettings.autoSave') as FormGroup;
    }

    get topicWorkFlowSettings(): FormGroup {
        return this.workflowSettingsForm.get('topicWorkFlowSettings') as FormGroup;
    }

    /**
     * Action when the user clicks the "OK" button
     */
    save(): void {
        if (this.submitted){
            return;
        }
        this.tagIsValid = this.workflowSettingsForm.valid;

        this.validateName();
        if (!this.nameIsValid || !this.tagIsValid) {
            return;
        }
        this.submitted = true;

        this._search.name = this.topicName;
        this._search.searchIntent = this.searchIntent || null;

        if (this.selectedGroup) {
            this._search.groupId = this.selectedGroup.id;
        }

        this.topicsService.createTopic(this._search, true).subscribe(
            () => {
                this.closeDialog();
            }
        );
    }

    /**
     * Action when the user clicks the "Cancel" button, or closes the dialog via the "X" in the header
     */
    cancel(): void {
        this.closeDialog();
    }

    /**
     * Validates the topic name
     */
    validateName(): void {
        this.nameIsValid = true;

        if (this.topicName.trim().length === 0) {
            this.nameIsValid = false;
        }

        if (this.topics?.some(topic => topic.name === this.topicName)) {
            this.nameIsValid = false;
        }
    }

    /**
     * Clones search model
     */
    private cloneSearch() {
        if (!this._search) {
            return;
        }

        if (this._saveAsNew) {
            this._search = this._search.clone();
            this.topicName = '';
        }
    }

    /**
     * Sets the appropriate localizations dependant on state
     */
    private setLocalizations(): void {
        if (this._saveAsNew) {
            this.localizations = new SaveTopicDialogLocalizationsModel(
                'SaveTopicDialog.saveAsNew.heading.saveNewTopic',
                'SaveTopicDialog.saveAsNew.topicNameLabel',
                'SaveTopicDialog.saveAsNew.topicNameHint',
                'SaveTopicDialog.saveAsNew.topicWorkflowSettings'
            );
        } else {
            this.localizations = new SaveTopicDialogLocalizationsModel(
                'SaveTopicDialog.heading.saveNewTopic',
                'SaveTopicDialog.topicNameLabel',
                'SaveTopicDialog.topicNameHint',
                'SaveTopicDialog.topicWorkflowSettings'
            );
        }
    }

    /**
     * Handles logic to update a change in search model
     */
    private searchModelChanged(search: MentionSearchModel): void {
        this._search = search;

        if (!this._search) {
            return;
        }

        this.topicName = !this._search.name ? '' : this._search.name;
        this.searchIntent = this._search.searchIntent || '';

        this.autoTags.patchValue({
            isAutoTaggingSearch:  this.accountAutoTaggingMaxReached ? null : this._search.autoTaggingSearch,
            tagIds:  this.accountAutoTaggingMaxReached ? [] : this._search.applyTagIds
        });

        this.autoSave.patchValue({
            saveEnabled: this._search.autoSavingConfig?.enabled,
            startDate: this._search.autoSavingConfig?.startDate,
            endDate: this._search.autoSavingConfig?.endDate,
            tagIds: this._search.autoSavingConfig?.tagIds,
            folderIds: this._search.autoSavingConfig?.folderIds,
            timezone: this._search.autoSavingConfig?.timezone
        });

        this.cloneSearch();
    }

    createNewGroup(name: string) {
        const group = new DisplayGroupModel();
        group.name = name;
        this.displayGroupsService.createGroup(group).subscribe({
            next: (id) => {
                group.id = id;
                this.selectedGroup = group;
            }
        });
    }

    onAddToGroupChange() {
        if (!this.addToGroup) {
            this.selectedGroup = null;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
